import type { Result } from 'true-myth';

import type { CompanySettings, CompanySettingsUpdate } from '~/shared/data-contract/company-settings';
import { companySettingsSchema } from '~/shared/data-contract/company-settings';
import type { OrganizationLanguage, OrganizationStatistics } from '~/shared/data-contract/organization-language';
import {
    organizationLanguagesResponseSchema,
    organizationStatisticsSchema,
} from '~/shared/data-contract/organization-language';
import type {
    SubscriptionItem,
    SubscriptionItemStatus,
    SubscriptionResponse,
    SubscriptionUpdateResult,
} from '~/shared/data-contract/subscription-item';
import {
    subscriptionItemsSchema,
    subscriptionResponseSchema,
    subscriptionUpdateResultSchema,
} from '~/shared/data-contract/subscription-item';
import type { HttpFetcherWithBaseUrl } from '~/shared/http-fetcher';
import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

type JsonFetcher = ValidatingHttpFetcherWithBaseUrl;

export type CompanySettingsFetchers = {
    readonly createOrUpdateSubscription: (args: {
        readonly items: readonly { readonly priceId: string; readonly quantity: number }[];
    }) => Promise<Result<SubscriptionUpdateResult, HttpFetcherError>>;
    readonly fetchCompanySettings: () => Promise<Result<CompanySettings, HttpFetcherError>>;
    readonly fetchOrganizationLanguages: () => Promise<Result<readonly OrganizationLanguage[], HttpFetcherError>>;
    readonly fetchOrganizationStatistics: () => Promise<Result<OrganizationStatistics, HttpFetcherError>>;
    readonly fetchSubscription: () => Promise<Result<SubscriptionResponse, HttpFetcherError>>;
    readonly fetchSubscriptionItems: (args?: {
        readonly language?: string;
        readonly status?: readonly SubscriptionItemStatus[];
    }) => Promise<Result<readonly SubscriptionItem[], HttpFetcherError>>;
    readonly removeSubscriptionItem: (args: { readonly priceId: string }) => Promise<Result<void, HttpFetcherError>>;
    readonly updateCompanySettings: (args: {
        readonly payload: CompanySettingsUpdate;
    }) => Promise<Result<CompanySettings, HttpFetcherError>>;
};

export type CompanySettingsFetchersDependencies = {
    readonly cobrainerApiFetcher: HttpFetcherWithBaseUrl;
    readonly validatingCobrainerApiFetcher: JsonFetcher;
};

export const createCompanySettingsFetchers = (
    dependencies: CompanySettingsFetchersDependencies,
): CompanySettingsFetchers => {
    const { cobrainerApiFetcher, validatingCobrainerApiFetcher } = dependencies;

    const fetchCompanySettings: CompanySettingsFetchers['fetchCompanySettings'] = async () => {
        return validatingCobrainerApiFetcher.get(companySettingsSchema, {
            pathname: '/company-settings',
        });
    };

    const fetchOrganizationLanguages: CompanySettingsFetchers['fetchOrganizationLanguages'] = async () => {
        const result = await validatingCobrainerApiFetcher.get(organizationLanguagesResponseSchema, {
            pathname: '/organization/languages',
            requestOptions: {
                searchParams: {
                    enabled: true,
                },
            },
        });

        return result.map((response) => response.languages);
    };

    const fetchOrganizationStatistics: CompanySettingsFetchers['fetchOrganizationStatistics'] = async () => {
        return validatingCobrainerApiFetcher.get(organizationStatisticsSchema, {
            pathname: '/job-architectures/statistics',
        });
    };

    const createOrUpdateSubscription: CompanySettingsFetchers['createOrUpdateSubscription'] = async ({ items }) => {
        return validatingCobrainerApiFetcher.putJson(subscriptionUpdateResultSchema, {
            pathname: '/billing/subscription',
            payload: { items },
        });
    };

    const fetchSubscription: CompanySettingsFetchers['fetchSubscription'] = async () => {
        return validatingCobrainerApiFetcher.get(subscriptionResponseSchema, {
            pathname: '/billing/subscription',
        });
    };

    const fetchSubscriptionItems: CompanySettingsFetchers['fetchSubscriptionItems'] = async (args) => {
        const searchParams = new URLSearchParams();
        for (const s of args?.status ?? []) {
            searchParams.append('status', s);
        }

        return validatingCobrainerApiFetcher.get(subscriptionItemsSchema, {
            pathname: '/billing/subscription/items',
            requestOptions: {
                ...(args?.language !== undefined && { headers: { 'Accept-Language': args.language } }),
                searchParams,
            },
        });
    };

    const removeSubscriptionItem: CompanySettingsFetchers['removeSubscriptionItem'] = async ({ priceId }) => {
        const result = await cobrainerApiFetcher.delete({
            pathname: `/billing/subscription/items/${priceId}`,
        });
        return result.map(() => undefined);
    };

    const updateCompanySettings: CompanySettingsFetchers['updateCompanySettings'] = async ({ payload }) => {
        return validatingCobrainerApiFetcher.patchJson(companySettingsSchema, {
            pathname: '/company-settings',
            payload,
        });
    };

    return {
        createOrUpdateSubscription,
        fetchCompanySettings,
        fetchOrganizationLanguages,
        fetchOrganizationStatistics,
        fetchSubscription,
        fetchSubscriptionItems,
        removeSubscriptionItem,
        updateCompanySettings,
    };
};
